import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, queryKeys, ApiError } from '../lib/api.js';
import type { AuthUser } from '../../../shared/types.js';

interface AuthResponse {
  user: AuthUser;
}

export function useAuth() {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.auth.me(),
    queryFn: () => api.get<AuthResponse>('/api/auth/me'),
    retry: false,
    staleTime: 60_000,
  });

  const loginMutation = useMutation({
    mutationFn: (credentials: { email: string; password: string }) =>
      api.post<AuthResponse>('/api/auth/login', credentials),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.auth.me(), data);
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => api.post('/api/auth/logout'),
    onSuccess: () => {
      queryClient.clear();
      window.location.href = '/login';
    },
  });

  const user = data?.user ?? null;
  const isAuthenticated = !!user && !(error instanceof ApiError && error.status === 401);

  return {
    user,
    isLoading,
    isAuthenticated,
    login: loginMutation.mutateAsync,
    logout: logoutMutation.mutate,
    loginError: loginMutation.error,
    isLoggingIn: loginMutation.isPending,
  };
}
